/**
 * Lazy-loadable feature bundle for `<LazyMotion>`.
 *
 * Why this file exists at all:
 *
 *   `<LazyMotion features={domAnimation}>` (the synchronous form) statically
 *   pulls framer-motion's projection layer (`create-projection-node`,
 *   `MeasureLayout`, drag base, …) into whatever chunk imports it — about
 *   ~20 KiB gzip that the landing page never actually animates with.
 *
 *   `<LazyMotion features={() => import('framer-motion').then(...)}>`
 *   wouldn't help on its own, because every component that uses LazyMotion
 *   ALSO statically imports `LazyMotion` and `m` from `framer-motion` at
 *   the top of the file — webpack will see `framer-motion` is already in
 *   the initial graph and won't split it.
 *
 *   Routing the dynamic import through `framer-motion-features-impl.ts`
 *   (a separate compilation unit whose ONLY static reference is this
 *   file's `import()`) lets webpack create a *separate* async chunk that
 *   contains only `domAnimation` and the projection / animation feature
 *   implementations it transitively pulls in. The component file's static
 *   `import { LazyMotion, m } from 'framer-motion'` keeps the lightweight
 *   `m` / `LazyMotion` primitives in the initial chunk, and the heavy
 *   feature code defers until after first paint, when LazyMotion's
 *   `useEffect` fires.
 *
 *   For the split to actually happen, `next.config.mjs` also has to leave
 *   async chunks out of the `framerMotion` cache group (`chunks: 'initial'`
 *   instead of `'all'`). With `'all'` + `enforce: true`, webpack lumps
 *   every framer-motion module — including this dynamic import target —
 *   back into the same shared chunk, defeating the split.
 *
 * Usage:
 *
 *   ```tsx
 *   import { LazyMotion, m } from 'framer-motion';
 *   import loadFramerMotionFeatures from '@/utils/framer-motion-features';
 *
 *   <LazyMotion features={loadFramerMotionFeatures} strict>
 *     <m.div animate={{ opacity: 1 }} />
 *   </LazyMotion>
 *   ```
 *
 *   The `loadFramerMotionFeatures` reference is intentionally hoisted to
 *   module scope so its identity is stable across renders.
 */
const loadFramerMotionFeatures = () =>
  import('@/utils/framer-motion-features-impl').then(mod => mod.default);

export default loadFramerMotionFeatures;
